import { cn } from "cn";
import { useState } from "react";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { BwCheckmarkLine } from "@/components/ui/icons";

import {
  ActiveFilterChip,
  EMPTY_VALUE_LABEL,
} from "../_components/active-filter-chip";
import { ValueIconStack } from "../_components/value-icon-stack";
import { useFilters } from "../_hooks/use-filters";
import type { FilterField, FilterOption } from "../types";

/**
 * Single-choice select. The field MUST provide `useOptions`. Subsumes the
 * status / type / plan / state per-feature chip+popover pairs.
 */
export const SingleSelectFilter = ({ field }: { field: FilterField }) => {
  const { filters, modify, clear } = useFilters();
  const value = (filters as Record<string, unknown>)[field.key] as
    | string
    | undefined;
  // select fields always declare useOptions
  const { options } = field.useOptions!();
  const [open, setOpen] = useState(() => value === undefined || value === "");

  const selected = options.find((option) => option.value === value);
  // Show the selected option's icon in the chip value slot (e.g. the priority
  // flame), mirroring the entity widget — not just the bare label.
  const valueLabel = selected ? (
    <span className="flex gap-1 items-center">
      <ValueIconStack options={[selected]} />
      <span>{selected.label}</span>
    </span>
  ) : (
    value || EMPTY_VALUE_LABEL
  );

  // Selected option floats to the top (separated), mirroring the other widgets.
  const selectedOptions = options.filter((option) => option.value === value);
  const unselectedOptions = options.filter((option) => option.value !== value);

  const renderOption = (option: FilterOption) => (
    <CommandItem
      key={option.value}
      onSelect={() => {
        modify({ [field.key]: option.value });
        setOpen(false);
      }}
      className={cn(
        /* sorted */
        "flex",
        "items-center justify-between",
        "cursor-pointer",
      )}
    >
      <span className="flex items-center gap-2 min-w-0">
        {option.icon}
        {option.detail ?? (
          <span className="line-clamp-1 break-all">{option.label}</span>
        )}
      </span>
      {value === option.value && (
        <BwCheckmarkLine className="size-4 shrink-0" />
      )}
    </CommandItem>
  );

  return (
    <ActiveFilterChip
      field={field}
      valueLabel={valueLabel}
      selectedCount={value ? 1 : 0}
      open={open}
      onOpenChange={setOpen}
      onRemove={() => clear(field.key)}
    >
      <Command
        className={cn(
          /* sorted */
          "bg-bg-surface-elevate-low",
          field.popoverWidthClassName ?? "w-50",
        )}
        shouldFilter={false}
      >
        <CommandList>
          {selectedOptions.length > 0 && (
            <CommandGroup>{selectedOptions.map(renderOption)}</CommandGroup>
          )}
          {selectedOptions.length > 0 && unselectedOptions.length > 0 && (
            <CommandSeparator />
          )}
          {unselectedOptions.length > 0 && (
            <CommandGroup>{unselectedOptions.map(renderOption)}</CommandGroup>
          )}
        </CommandList>
      </Command>
    </ActiveFilterChip>
  );
};
